import {
  UseMutationOptions,
  UseMutationResult,
  useMutation
} from '@tanstack/react-query'
import endpoints from 'const/apiEndpoints'
import queryClient from 'const/queryClient'
import { creditAccountsCacheKey } from '@syf-api-library/credit-accounts'
import { TError, getFetchWrapper } from '@syf-api-library/core'
import { MFEConfig } from 'syf-js-utilities'

interface AddAccountResponse {
  success: boolean
}

export interface AddAccountVariables {
  accountNumber: string
  dateOfBirth: string
  leadAccountId?: string
  identifier: string
}

interface AddAccountMutationOptions<
  TData,
  TVariables = AddAccountVariables,
  TContext = unknown
> {
  config: MFEConfig
  mutationOptions?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >
}

export const useAddAccountMutation = ({
  config,
  mutationOptions = {}
}: AddAccountMutationOptions<
  AddAccountResponse,
  AddAccountVariables
>): UseMutationResult<AddAccountResponse, TError, AddAccountVariables> => {
  const fetchWrapper = getFetchWrapper(config)
  const addAccountURL = new URL(
    endpoints.ADD_MISSING_ACCOUNT,
    config.hosts.apigee
  )

  const mutationFunction = async (
    payloadData: AddAccountVariables
  ): Promise<AddAccountResponse> => {
    const { accountNumber, identifier, dateOfBirth, leadAccountId } =
      payloadData

    const apiUrl = leadAccountId
      ? `${addAccountURL}?cipher.accountId=${leadAccountId}&accountIdType=guid`
      : addAccountURL

    await fetchWrapper(apiUrl, {
      method: 'POST',
      body: JSON.stringify({
        'cipher.accountNumber': accountNumber,
        'cipher.ssn': identifier,
        'cipher.dateOfBirth': dateOfBirth
      })
    })

    return { success: true }
  }

  return useMutation<AddAccountResponse, TError, AddAccountVariables>({
    mutationFn: mutationFunction,
    onSuccess: () => {
      queryClient.invalidateQueries(creditAccountsCacheKey())
    },
    ...mutationOptions
  })
}
